import type { DcCircuitTopology, DcPort } from "./dc-circuit-topology";
import type { CurrentAlternative, CurrentChoiceGroup, SignedDutyCurrent } from "./signed-current-ledger";
import { sumCurrentCase } from "./signed-current-ledger";

export type DesignPortCurrent = { port: DcPort; current: SignedDutyCurrent };
export type DesignDeviceMode = { id: string; ports: DesignPortCurrent[] };
export type UnresolvedCurrentReason = "missing-return" | "unpaired-port" | "shorted-rails" | "no-source" | "parallel-paths" | "source-route-limit" | "source-range-limit" | "source-choice-limit" | "shared-port-rating" | "unsupported-component-category" | "ambiguous-current-role" | "missing-load-current" | "unsupported-pv-array" | "one-way-device" | "restricted-device-use" | "converter-current-coupling";
export type UnresolvedCurrentComponent = { nodeId: string; portId?: string; reason: UnresolvedCurrentReason };

/** A complete source loop supplies a signed transfer stencil. Choosing a
 * source is a design extremum, never an assertion of equal current sharing.
 * Unspecified parallel-copper sharing is retained as an explicit fallback;
 * one deterministic path is not evidence that all other wires carry zero. */
export function resolveDesignCurrentGroup(
  topology: DcCircuitTopology,
  nodeId: string,
  modes: readonly DesignDeviceMode[],
): { group?: CurrentChoiceGroup; unresolved: UnresolvedCurrentComponent[]; sourceAllocationDependentCableIds: ReadonlySet<string> } {
  const alternatives: CurrentAlternative[] = [{ id: `${nodeId}:off`, byCable: new Map() }];
  const unresolved: UnresolvedCurrentComponent[] = [];
  const sourceAllocationDependentCableIds = new Set<string>();
  for (const mode of modes) {
    let choices: CurrentAlternative[] = [{ id: mode.id, byCable: new Map() }];
    for (const { port, current } of mode.ports) {
      const resolution = topology.resolvePort(port);
      if (resolution.unsupported.length) {
        unresolved.push(...resolution.unsupported.map(item => ({ nodeId, portId: port.id, reason: item.reason })));
        continue;
      }
      // A battery-backed bus is sized from its battery source paths. Treating
      // both a charger's rated output and that same output as every load's
      // independent source would count the charger's power stage twice.
      const batteryRoutes = resolution.routes.filter(route => route.source.kind === "battery");
      // An output stage is a source, not evidence that it can absorb reverse
      // power from another charger. Only batteries are receiving routes for
      // an output-role (negative) transfer until bidirectionality is explicit.
      const isOutputTransfer = current.sustainedA < 0 || current.peakA < 0;
      const routes = batteryRoutes.length ? batteryRoutes : isOutputTransfer ? [] : resolution.routes;
      if (!routes.length) {
        unresolved.push({ nodeId, portId: port.id, reason: resolution.incomplete[0]?.reason ?? "no-source" });
        continue;
      }
      const portChoices: CurrentAlternative[] = [];
      for (const [index, route] of routes.entries()) {
        const steps = [...route.positive, ...route.negative, ...route.series];
        if (steps.some(step => hasAlternateCopperPath(topology, step.cableId, step.from, step.to))) {
          unresolved.push({ nodeId, portId: port.id, reason: "parallel-paths" });
          continue;
        }
        const byCable = new Map<string, SignedDutyCurrent>();
        for (const step of steps) {
          const previous = byCable.get(step.cableId);
          byCable.set(step.cableId, {
            sustainedA: (previous?.sustainedA ?? 0) + step.direction * current.sustainedA,
            startingA: (previous?.startingA ?? 0) + step.direction * current.startingA,
            peakA: (previous?.peakA ?? 0) + step.direction * current.peakA,
          });
        }
        portChoices.push({ id: `${port.id}:${route.sourceId}:${index}`, byCable });
      }
      if (choices.length * portChoices.length > 128) {
        unresolved.push({ nodeId, portId: port.id, reason: "source-choice-limit" });
        continue;
      }
      choices = choices.flatMap(choice => portChoices.map(next => ({
        id: `${choice.id}|${next.id}`, byCable: sumCurrentCase([choice, next]),
      })));
    }
    // Compare only source choices within one operating mode. Off, start/run
    // and inverter/charger modes are intentional device behaviour, not
    // uncertainty about allocation among parallel sources. A common trunk
    // has identical signed contributions and needs no allocation warning.
    const ids = new Set(choices.flatMap(choice => [...choice.byCable.keys()]));
    for (const id of ids) {
      const first = choices[0]?.byCable.get(id);
      if (choices.some(choice => {
        const current = choice.byCable.get(id);
        return (["sustainedA", "startingA", "peakA"] as const).some(key =>
          Math.abs((current?.[key] ?? 0) - (first?.[key] ?? 0)) > 1e-9);
      })) sourceAllocationDependentCableIds.add(id);
    }
    alternatives.push(...choices);
  }
  return unresolved.length
    ? { unresolved, sourceAllocationDependentCableIds: new Set() }
    : { group: { id: nodeId, alternatives }, unresolved, sourceAllocationDependentCableIds };
}

const alternatePathCache = new WeakMap<DcCircuitTopology, Map<string, boolean>>();
function hasAlternateCopperPath(topology: DcCircuitTopology, cableId: string, from: string, to: string): boolean {
  let cache = alternatePathCache.get(topology);
  if (!cache) { cache = new Map(); alternatePathCache.set(topology, cache); }
  const known = cache.get(cableId);
  if (known != null) return known;
  const seen = new Set([from]), queue = [from];
  for (let i = 0; i < queue.length; i++) for (const edge of topology.graph.adj.get(queue[i]) ?? []) {
    if (edge.via.kind === "cable" && edge.via.cableId === cableId) continue;
    if (seen.has(edge.vertex)) continue;
    if (edge.vertex === to) { cache.set(cableId, true); return true; }
    seen.add(edge.vertex); queue.push(edge.vertex);
  }
  cache.set(cableId, false);
  return false;
}
